import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { SchedulerService } from 'src/app/scheduler/services/scheduler.service';
import { InsuranceDropdownVisibility } from 'src/app/shared/enums/insurance-dropdown-visibility.enum';
import { Office } from '../../../shared/domains/scheduler/office/office';
import { AppointmentRole } from '../../../shared/enums/appointment-role.enum';
import { ReplaySubject, Subscription, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppointmentDetailsService } from '../../../core/services/appointment-details.service';
import { DropdownValueObject } from '../../../shared/components/dropdown/dropdown-value-object';
import { AppMatSelectComponent } from '../../../shared/components/dropdown/app-mat-select.component';
import { NgIf } from '@angular/common';
import { AppointmentType } from '../../../shared/domains/scheduler/office/appointment-type';

@Component({
  selector: 'eos-service-options-section',
  templateUrl: './service-options-section.component.html',
  styleUrls: ['./service-options-section.component.scss'],
  standalone: true,
  imports: [NgIf, AppMatSelectComponent],
})
export class ServiceOptionsSectionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private schedulerService = inject(SchedulerService);
  private appointmentDetailsService = inject(AppointmentDetailsService);
  private logger = inject(NGXLogger);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  @Output() insuranceUpdated = new EventEmitter<void>();
  @Output() providerUpdated = new EventEmitter<void>();

  insuranceDropdownVisibility = InsuranceDropdownVisibility;
  insurancesDropdown: DropdownValueObject[] = [];
  providersDropdown: DropdownValueObject[] = [];
  selectedInsurance: DropdownValueObject | null;
  selectedProvider: DropdownValueObject | null;
  private appointmentTypeSub: Subscription;
  insuranceCarrierLabel: string;
  appointmentWithDoctorLabel: string;
  unsubscribe: ReplaySubject<void> = new ReplaySubject<void>(1);

  ngOnInit(): void {
    // The appointmentType will affect the setup of the provider dropdown
    this.appointmentTypeSub = this.schedulerService.appointmentType$.subscribe(
      (apptType) => {
        this.logger.debug('Received appointment type:', apptType);

        this.handleAppointmentTypeChange(apptType);
      },
    );

    this.setupInsurerSelection();
  }

  ngOnDestroy(): void {
    // Unsubscribe when the component is destroyed
    this.appointmentTypeSub.unsubscribe();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngAfterViewInit(): void {
    this.getDropdownTranslatedLabel();
  }

  getDropdownTranslatedLabel(): void {
    this.translate
      .get('serviceOptions.insuranceCarrier')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.insuranceCarrierLabel = value;
      });

    this.translate
      .get('serviceOptions.appointmentWithDoctor')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.appointmentWithDoctorLabel = value;
      });

    this.cdr.detectChanges();
  }

  private handleAppointmentTypeChange(
    apptType: AppointmentType | null | undefined,
  ): void {
    this.logger.debug('=== handleAppointmentTypeChange()', apptType);
    if (!apptType) {
      this.providersDropdown = [];
      return;
    }

    this.populateProvidersDropdown(apptType);
    this.setupProviderSelection();
  }

  populateProvidersDropdown(apptType: AppointmentType): void {
    const location = this.appointmentDetailsService.getLocation();
    const providers =
      apptType.role === AppointmentRole.PROVIDER
        ? location.providers
        : location.staffs;

    this.providersDropdown = providers.map(
      (provider) => new DropdownValueObject(provider.id, provider.displayName),
    );
  }

  private setupInsurerSelection(): void {
    if (this.appointmentDetailsService.getLocation()) {
      const office: Office = this.appointmentDetailsService.getLocation();
      this.insurancesDropdown = office?.insurances?.map(
        (insurance) =>
          new DropdownValueObject(insurance.id, insurance.displayName),
      );

      if (
        this.appointmentDetailsService.getUseLastServiceDetail() &&
        this.appointmentDetailsService.getInsuranceDVO()
      ) {
        this.selectedInsurance =
          this.appointmentDetailsService.getInsuranceDVO()!;
        this.updateInsurance(this.selectedInsurance);
      }
    }
  }

  private setupProviderSelection(): void {
    // If there is only one provider, select it by default
    if (this.providersDropdown.length === 1) {
      this.selectedProvider = this.providersDropdown[0];
      this.updateProvider(this.providersDropdown[0]);
      return;
    }

    const office = this.appointmentDetailsService.getLocation();

    // Check if "No Preference" should be added based on office configuration
    if (office?.defaultNoPreference) {
      // Add a "No Preference" option to the dropdown
      this.translate
        .get('serviceOptions.noPreferences')
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((translatedNoPreference: string) => {
          this.providersDropdown.unshift(
            new DropdownValueObject('-1', translatedNoPreference),
          );
        });

      this.selectInitialProvider();
    } else {
      // When defaultNoPreference is false, don't add "No Preference" option
      // Check if there's a previously selected provider
      const lastSelectedProvider =
        this.appointmentDetailsService.getProviderDVO();

      if (this.checkIfProviderIsInDropdown(lastSelectedProvider)) {
        this.selectedProvider = lastSelectedProvider;
        this.updateProvider(this.selectedProvider);
      } else {
        // Leave selectedProvider as null (no default selection)
        this.selectedProvider = null;
      }
    }
  }

  private selectInitialProvider(): void {
    const lastSelectedProvider =
      this.appointmentDetailsService.getProviderDVO();

    // Select either the previously selected provider or the added 'No Preference' option
    if (this.checkIfProviderIsInDropdown(lastSelectedProvider)) {
      this.selectedProvider = lastSelectedProvider;
    } else {
      this.selectedProvider = this.providersDropdown[0];
    }

    this.updateProvider(this.selectedProvider);
  }

  checkIfProviderIsInDropdown(provider: DropdownValueObject): boolean {
    return !!this.providersDropdown.find(
      (item) => item.value === provider?.value,
    );
  }

  updateInsurance(newSelectedInsurance: DropdownValueObject): void {
    this.logger.debug('updateInsurance()', newSelectedInsurance);
    this.appointmentDetailsService.setInsuranceDVO(newSelectedInsurance);
    this.insuranceUpdated.emit(); // Notify parent of the update
    this.appointmentDetailsService.dumpAppointmentDetails();
  }

  updateProvider(selectedProvider: DropdownValueObject): void {
    this.logger.debug('updateLocation()', selectedProvider);
    this.appointmentDetailsService.setProviderDVO(selectedProvider);
    this.providerUpdated.emit();
    this.appointmentDetailsService.dumpAppointmentDetails();
  }

  locationInsuranceRequired(): string {
    if (
      !this.appointmentDetailsService.getLocation() ||
      !this.appointmentDetailsService.getLocation().insuranceRequired
    ) {
      return InsuranceDropdownVisibility.REQUIRED;
    } else {
      return this.appointmentDetailsService.getLocation().insuranceRequired;
    }
  }

  getSelectedProvider(): DropdownValueObject | null {
    return this.selectedProvider;
  }
}
